import React, { useRef, useEffect } from 'react';
import Transition from '../utils/Transition';

function NewAssessmentModal({
  children,
  id,
  title,
  modalOpen,
  setModalOpen,
  isMaximized,
  toggleMaximize
}) {

  const modalContent = useRef(null);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      // Don't close if the modal isn't open
      if (!modalOpen) return;
      
      // Don't close if clicking inside the modal content
      if (modalContent.current?.contains(target)) return;
      
      // Don't close if clicking on a file input or during file selection
      if (target.tagName === 'INPUT' && target.type === 'file') return;
      
      // Don't close if the click is related to file selection dialog
      if (target.closest('.file-upload-container')) return;
      
      setModalOpen(false);
    };
    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 transition-opacity"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-50 overflow-hidden flex items-center my-4 justify-center px-4 sm:px-6"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart="opacity-0 translate-y-4"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd="opacity-0 translate-y-4"
      >
        <div ref={modalContent} className={`bg-white dark:bg-slate-700 rounded shadow-lg overflow-x-auto ${isMaximized ? 'max-w-6xl w-full' : 'max-w-8xl'} max-h-full`}>
          {/* Modal header */}
          <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex justify-between items-center">
              <div className="font-semibold text-slate-800 dark:text-slate-100">{title}</div>
              <div className="flex items-center space-x-2">
                {/* Maximize button */}
                <button onClick={toggleMaximize} className="text-slate-400 dark:text-slate-500 hover:text-slate-500 dark:hover:text-slate-400">
                  <div className="sr-only">{isMaximized ? 'Minimize' : 'Maximize'}</div>
                  <svg className="w-4 h-4 stroke-current fill-none" viewBox="0 0 16 16" strokeWidth="1.5" strokeLinecap="square">
                    {isMaximized ? (
                      // When maximized, show outward arrows to indicate you can restore down
                      <path d="M2 5V2H5M14 5V2H11M2 11V14H5M14 11V14H11"/>
                    ) : (
                      // When normal size, show inward arrows to indicate you can maximize
                      <path d="M14 2L11 5M2 14L5 11M14 14L11 11M2 2L5 5"/>
                    )}
                  </svg>
                </button>
                {/* Close button */}
                <button className="text-slate-400 dark:text-slate-500 hover:text-slate-500 dark:hover:text-slate-400" onClick={(e) => { e.stopPropagation(); setModalOpen(false); }}>
                  <div className="sr-only">Close</div>
                  <svg className="w-4 h-4 fill-current">
                    <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {children}     
        </div>        
      </Transition>
    </>
  );
}

export default NewAssessmentModal;
